import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Box, Typography, CircularProgress } from '@mui/material';
import * as api from '../../services/api';

interface MobileUploadProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route cho mobile upload - chỉ cho phép user có mobileUploadEnabled = true
 */
const MobileUploadProtectedRoute: React.FC<MobileUploadProtectedRouteProps> = ({ children }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [mobileUploadEnabled, setMobileUploadEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkMobileUploadStatus = async () => {
      try {
        // Admin và Staff luôn có quyền truy cập
        if (user?.role === 'ADMIN' || user?.role === 'STAFF') {
          setMobileUploadEnabled(true);
          setLoading(false);
          return;
        }

        if (user?.role === 'USER' || user?.role === 'STAFF_USER') {
          const response = await api.getMobileUploadStatus();
          setMobileUploadEnabled(response.mobileUploadEnabled || false);
        } else {
          setMobileUploadEnabled(false);
        }
      } catch (error) {
        console.error('Error checking mobile upload status:', error);
        // Nếu có lỗi, mặc định là không có quyền
        setMobileUploadEnabled(false);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      checkMobileUploadStatus();
    } else {
      setLoading(false);
      setMobileUploadEnabled(false);
    }
  }, [user]);

  // Hiển thị loading khi đang check
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body1">Đang kiểm tra quyền truy cập...</Typography>
      </Box>
    );
  }

  // Nếu không có quyền, redirect về trang chủ
  if (!mobileUploadEnabled) {
    return <Navigate to="/" replace />;
  }

  // Có quyền, render children
  return <>{children}</>;
};

export default MobileUploadProtectedRoute;

